/**
 * useAttentionManager (KAN-828)
 *
 * Менеджер клиента из строки блока "Требуют внимания" без закрытия строки.
 * Сохранение - тот же запрос, что в карточке клиента (useAssignClientManager:
 * PATCH /clients/{id}/ manager_id, null снимает менеджера); после него
 * обновляются блок внимания, клиенты и карточка клиента.
 * Права в одном месте:
 *   - действие есть, только пока пользователю открыт этот PATCH (KAN-811 api_access);
 *   - менять ли менеджера этого клиента, решает clientManagerRights (обычный менеджер не трогает чужих клиентов,
 *     старший менеджер, владелец и оператор меняют любых).
 * pending нужен строке: пока менеджер сохраняется, её кнопки закрытия недоступны.
 */
import { assignErrorMessage } from '@/components/managers/assignErrorMessage'
import { useCanCallApi } from '@/hooks/useApiAccess'
import { useClientManagerRights } from '@/hooks/useClientManagerRights'
import { useAssignClientManager } from '@/hooks/useManagers'
import type { AttentionItem } from '@/services/api/dashboard'

export interface AttentionManager {
  /** Выбор менеджера показывается (PATCH клиента открыт пользователю). */
  canAssign: boolean
  /** Менеджера этого клиента можно сменить или снять. */
  canChange: boolean
  /** Почему менять нельзя (подсказка под выбором); null: можно. */
  lockedHint: string | null
  assign: (managerId: string | null) => void
  pending: boolean
  /** Текст ошибки сохранения под селектом; null: ошибки нет. */
  error: string | null
}

export function useAttentionManager(item: Pick<AttentionItem, 'client_id' | 'manager'>): AttentionManager {
  const canAssign = useCanCallApi('PATCH', `/api/v1/clients/${item.client_id}/`)
  const rights = useClientManagerRights(item.manager?.id)
  const mutation = useAssignClientManager(item.client_id)
  return {
    canAssign,
    canChange: rights.canChange,
    lockedHint: rights.lockedHint,
    assign: (managerId) => mutation.mutate(managerId),
    pending: mutation.isPending,
    error: assignErrorMessage(mutation.error),
  }
}
